import { useEffect, useState } from "react";
import { ExternalLink, Info } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { WFPButton } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";

import client from "../../../../client";
import { Deposit } from "../../../Deposits/DepositCard/deposit-dtos";

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

type MatchableDeposit = Deposit & { has_direct_offer_same_trader?: boolean };

export interface BrowseMarketParams {
  commodity_id: number;
  quality: string;
  unit_of_measure_id?: number;
}

interface MatchingDepositsPanelProps {
  commodityId: number;
  quality: string;
  unitOfMeasureId?: number;
  commodityLabel?: string;
  onBrowseMarket: (params: BrowseMarketParams) => void;
  t: TranslateFn;
}

export const MatchingDepositsPanel = ({
  commodityId,
  quality,
  unitOfMeasureId,
  commodityLabel,
  onBrowseMarket,
  t,
}: MatchingDepositsPanelProps) => {
  const [hasMatches, setHasMatches] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  useEffect(() => {
    let active = true;
    setIsFetching(true);

    client
      .get<{ results: MatchableDeposit[] }>("/api/deposits/", {
        params: {
          page_size: 99,
          page: 1,
          commodity_id: commodityId,
          ...(quality ? { quality } : {}),
          ...(unitOfMeasureId ? { unit_of_measure_id: unitOfMeasureId } : {}),
          is_sold: false,
        },
      })
      .then(response => {
        if (!active) {
          return;
        }
        const matches = (response.data.results ?? []).filter(
          deposit => !deposit.has_direct_offer_same_trader
        );
        setHasMatches(matches.length > 0);
      })
      .catch(() => {
        if (active) {
          setHasMatches(false);
        }
      })
      .finally(() => {
        if (active) {
          setIsFetching(false);
        }
      });

    return () => {
      active = false;
    };
  }, [commodityId, quality, unitOfMeasureId]);

  return (
    <div className="space-y-3 rounded-lg border border-border p-4">
      <h3 className="font-semibold text-foreground">{commodityLabel}</h3>

      {isFetching ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <Spinner className="size-4" />
          {t("common.loading")}
        </div>
      ) : hasMatches ? (
        <div className="space-y-3">
          <Alert variant="info">
            <Info />
            <AlertDescription>{t("marketOfferDetails.form.availableStocksMatch")}</AlertDescription>
          </Alert>
          <WFPButton
            variant="link"
            className="h-auto p-0"
            onClick={() =>
              onBrowseMarket({
                commodity_id: commodityId,
                quality,
                unit_of_measure_id: unitOfMeasureId,
              })
            }
          >
            {t("marketOfferDetails.form.goToAvailableStocks")}
            <ExternalLink className="size-4" />
          </WFPButton>
        </div>
      ) : (
        <p className="text-muted-foreground text-sm">
          {t("marketOfferDetails.form.noMatchingStockFound")}
        </p>
      )}
    </div>
  );
};
